import { useTranslations } from '@vocab/react';
import {
  Heading,
  Stack,
  Columns,
  Column,
  Box,
  Alert,
  Text,
} from 'braid-design-system';
import React, { type Ref, type RefObject, useRef, useState } from 'react';
import { shallowEqual, useSelector, useDispatch } from 'react-redux';

import {
  SECTION_JOB_DESCRIPTION,
  SECTION_BRAND_SELECTION,
  SECTION_ABOUT_BUSINESS,
  SECTION_KEY_SELLING_POINTS,
  SECTION_CANDIDATE_SEARCH_RESULTS,
} from 'src/app/shared/constants/pageSectionName';
import {
  PAGE_ID_WRITE,
  PAGE_ID_SELECT_AD_TYPE,
} from 'src/app/shared/constants/pageUid';
import { EVENT_NAME_ALERT_VIEWED } from 'src/app/shared/dataAnalytics/schema/constants';
import {
  useFeatureToggleData,
  FEATURE_AI_AD_WRITING,
} from 'src/app/shared/featureToggle';
import handleValidation from 'src/app/utils/handleValidation';
import getTrackingAdapter from 'src/app/v1/dataAnalytics';
import { actionUpdateHasReviewedGeneratedContent } from 'src/app/v1/state/modules/session/hasReviewedGeneratedContent';
import { AboutBusiness } from 'src/app/v1/views/components/AboutBusiness';
import { AdWritingToggler } from 'src/app/v1/views/components/AdWritingToggler';
import { Branding } from 'src/app/v1/views/components/Branding';
import { BrandingContextualHelp } from 'src/app/v1/views/components/BrandingContextualHelp';
import { FieldHeading } from 'src/app/v1/views/components/FieldHeading';
import { Form, type FormRef } from 'src/app/v1/views/components/Form';
import { JobSummary } from 'src/app/v1/views/components/JobSummary';
import { KeySellingPoints } from 'src/app/v1/views/components/KeySellingPoints';
import { PageButtons } from 'src/app/v1/views/components/PageButtons';
import { ProductFeatureBadge } from 'src/app/v1/views/components/ProductFeatureBadge';
import { StandardWritingFlow } from 'src/app/v1/views/components/StandardWritingFlow';
import { Video } from 'src/app/v1/views/components/Video';
import { withDesktopEnhancers } from 'src/app/v1/views/enhancers/withDesktopEnhancers';
import { useBrandingFeature } from 'src/app/v1/views/hooks/useBrandingFeature';
import { useHandleEmailVerification } from 'src/app/v1/views/hooks/useHandleEmailVerification';
import { useJobContentGeneration } from 'src/app/v1/views/hooks/useJobContentGeneration';
import { useKeySellingPointsFeature } from 'src/app/v1/views/hooks/useKeySellingPointsFeature';
import PageLayout from 'src/app/v1/views/layouts/PageLayout';
import { ContinueWithoutEditingDialog } from 'src/app/v1/views/pages/Write/components/ContinueWithoutEditingDialog';
import { ReplaceWhatYouEnteredDialog } from 'src/app/v1/views/pages/Write/components/ReplaceWhatYouEnteredDialog';
import { PageSection } from 'src/app/v1/views/sections/PageSection';
import type { AppState } from 'src/types/appState';
import type {
  GuidedWritingFlowRef,
  StandardWritingFlowRef,
} from 'src/types/formFields';
import type {
  CreateFlowPageContainer,
  CreateFlowPageProps,
} from 'src/types/pages';

import translations from './.vocab';
import { JobContentGenerationLegalDisclaimer } from './components/JobContentGenerationLegalDisclaimer';
import { JobContentGenerationLoadingOverlay } from './components/JobContentGenerationLoadingOverlay';
import { WriteWithAICtaSection } from './components/WriteWithAICtaSection';

export const WritePage = (props: CreateFlowPageProps) => {
  const { t } = useTranslations(translations);
  const {
    handleDispatchDraft,
    handleNextPageRoute,
    handleProgrammaticRouteChange,
    handleSaveDraftFailed,
    pageErrorMessage,
  } = props;

  const {
    advertiserName,
    categoryText,
    jobLocationText,
    jobTitle,
    hasAIAssistanceBeenUsed,
    hasReviewedGeneratedContent,
    isGuided,
    isPrivateAdvertiser,
    subCategoryText,
    workType,
  } = useSelector(
    (state: AppState) => ({
      advertiserName: state.session.advertiserName,
      categoryText: state.session.categoryText,
      jobLocationText: state.session.jobLocationText,
      jobTitle: state.formData.jobTitle,
      hasAIAssistanceBeenUsed: state.formData.hasAIAssistanceBeenUsed,
      hasReviewedGeneratedContent: state.session.hasReviewedGeneratedContent,
      isPrivateAdvertiser: state.session.hirerAccount?.isPrivateAdvertiser,
      isGuided: state.formData?.isGuided,
      subCategoryText: state.session.subCategoryText,
      workType: state.formData.workType,
    }),
    shallowEqual,
  );

  const aboutBusinessRef: RefObject<any> = useRef();
  const guidedWritingFlowRef: Ref<GuidedWritingFlowRef> = useRef(null);
  const jobSummaryRef: RefObject<any> = useRef();
  const keySellingPointsRef: RefObject<any> = useRef();
  const standardWritingFlowRef: Ref<StandardWritingFlowRef> = useRef(null);
  const formRef = useRef<FormRef>(null);

  const [isLoading, setIsLoading] = useState(false);
  const [
    isReplaceWhatYouEnteredDialogOpen,
    setReplaceWhatYouEnteredDialogOpen,
  ] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [AIGenerationError, setAIGenerationError] = useState(false);
  const [
    isContinueWithoutEditingDialogOpen,
    setIsContinueWithoutEditingDialogOpen,
  ] = useState(false);

  const dispatch = useDispatch();

  const { handleEmailVerification } = useHandleEmailVerification();

  const containerComponentMapping: CreateFlowPageContainer[] = [
    {
      containerId: SECTION_ABOUT_BUSINESS,
      components: [aboutBusinessRef],
      validationCondition: () => isGuided,
    },
    {
      containerId: SECTION_JOB_DESCRIPTION,
      components: [guidedWritingFlowRef],
      validationCondition: () => isGuided,
    },
    {
      containerId: SECTION_JOB_DESCRIPTION,
      components: [standardWritingFlowRef],
      validationCondition: () => !isGuided,
    },
    {
      containerId: SECTION_CANDIDATE_SEARCH_RESULTS,
      components: [jobSummaryRef],
    },
    {
      containerId: SECTION_KEY_SELLING_POINTS,
      components: [keySellingPointsRef],
    },
  ];
  const handlePageValidation = async (skipIfNull = false) => {
    setIsLoading(true);
    await handleEmailVerification();
    const validationResult = handleValidation(
      containerComponentMapping,
      skipIfNull,
    );
    setIsLoading(false);
    return validationResult;
  };

  const onBackButtonClicked = () => {
    props.handleProgrammaticRouteChange(PAGE_ID_SELECT_AD_TYPE);
  };

  const isKeySellingPointsEnabled = useKeySellingPointsFeature();
  const isBrandingEnabled = useBrandingFeature();

  const isAiAdWritingEnabled = useFeatureToggleData(FEATURE_AI_AD_WRITING);
  const showGenerationCtaSection =
    !hasAIAssistanceBeenUsed && !isGenerating && !AIGenerationError;

  const { generateJobContent } = useJobContentGeneration();

  const shouldDisplayReplacementConfirmationDialogOnGenerateAI =
    (): boolean => {
      const isJobDescriptionExists = standardWritingFlowRef.current?.inputValue;
      const isJobSummaryExists = jobSummaryRef.current?.inputValue;

      return isJobDescriptionExists || isJobSummaryExists;
    };

  const generatingJobContent = async () => {
    try {
      setIsGenerating(true);
      await generateJobContent();
    } catch (error) {
      setAIGenerationError(true);
      getTrackingAdapter.sendLinkEvent({
        alertShownMessage:
          "We can't write your ad right now. We're doing our best to fix this. Try refreshing the page or check back later.",
        alertShownMessageCode: 'AI generation error',
        alertShownTone: 'critical',
        eventName: EVENT_NAME_ALERT_VIEWED,
        advertiserName: advertiserName || '',
        jobClassification: categoryText,
        jobLocation: jobLocationText,
        jobSubClassification: subCategoryText,
        jobTitle,
        jobWorkType: workType,
      });
      return;
    } finally {
      setIsGenerating(false);
    }
  };

  const onWriteWithAIClick = async () => {
    if (shouldDisplayReplacementConfirmationDialogOnGenerateAI()) {
      // show dialog
      setReplaceWhatYouEnteredDialogOpen(true);
    } else {
      await generatingJobContent();
    }
  };

  const handlePreSubmit = () => {
    const isShowContinueWithoutEditingDialog =
      hasAIAssistanceBeenUsed && !hasReviewedGeneratedContent;

    if (isShowContinueWithoutEditingDialog) {
      setIsContinueWithoutEditingDialogOpen(true);
    }

    return isShowContinueWithoutEditingDialog;
  };

  if (isAiAdWritingEnabled) {
    return (
      <>
        <ReplaceWhatYouEnteredDialog
          isOpen={isReplaceWhatYouEnteredDialogOpen}
          onCancel={() => {
            setReplaceWhatYouEnteredDialogOpen(false);
          }}
          onConfirm={async () => {
            setReplaceWhatYouEnteredDialogOpen(false);
            await generatingJobContent();
          }}
        />

        <ContinueWithoutEditingDialog
          isOpen={isContinueWithoutEditingDialogOpen}
          onCancel={() => {
            setIsContinueWithoutEditingDialogOpen(false);
          }}
          onConfirm={() => {
            dispatch(actionUpdateHasReviewedGeneratedContent(true));
            formRef.current?.onValidationSuccess();
          }}
        />

        <PageLayout
          className="write-page-layout"
          hasProgressBar
          handlePreSubmit={handlePreSubmit}
          handleProgrammaticRouteChange={handleProgrammaticRouteChange}
          handleValidation={handlePageValidation}
          pageErrorMessage={pageErrorMessage}
          handleDispatchDraft={handleDispatchDraft}
        >
          <Stack space="large">
            <Heading level="3" component="h2">
              {t('write-page-heading')}
            </Heading>
            <Form
              handleNextPageRoute={handleNextPageRoute}
              handlePreSubmit={handlePreSubmit}
              handleValidation={handlePageValidation}
              ref={formRef}
            >
              <Stack space="xlarge">
                <Stack space={isKeySellingPointsEnabled ? 'small' : 'large'}>
                  <PageSection sectionName={SECTION_JOB_DESCRIPTION}>
                    <Stack space="small">
                      <JobContentGenerationLoadingOverlay
                        isGenerating={isGenerating}
                      >
                        <Stack space="medium">
                          {showGenerationCtaSection && (
                            <WriteWithAICtaSection
                              onWriteWithAIClick={onWriteWithAIClick}
                            />
                          )}

                          {AIGenerationError && (
                            <Alert
                              tone="critical"
                              data={{ testid: 'ai-generation-error' }}
                            >
                              <Text>{t('write-page-ai-generation-error')}</Text>
                            </Alert>
                          )}

                          <Stack space="small">
                            <FieldHeading
                              heading={t(
                                'write-page-job-description-section-heading',
                              )}
                            />
                            <StandardWritingFlow ref={standardWritingFlowRef} />
                            <Stack space="xsmall">
                              <FieldHeading
                                heading={t(
                                  'write-page-job-summary-input-label',
                                )}
                                subHeading={t(
                                  'write-page-job-summary-text-description',
                                )}
                              />
                              <JobSummary ref={jobSummaryRef} />
                            </Stack>
                          </Stack>

                          {hasAIAssistanceBeenUsed && (
                            <Box
                              {...(isKeySellingPointsEnabled && {
                                marginBottom: 'medium',
                              })}
                            >
                              <JobContentGenerationLegalDisclaimer />
                            </Box>
                          )}
                        </Stack>
                      </JobContentGenerationLoadingOverlay>
                      {isKeySellingPointsEnabled && (
                        <KeySellingPoints ref={keySellingPointsRef} />
                      )}
                    </Stack>
                  </PageSection>

                  <PageSection sectionName={SECTION_BRAND_SELECTION}>
                    <Stack space="large">
                      {isBrandingEnabled && (
                        <Stack space="small">
                          <ProductFeatureBadge />
                          <Stack space="xsmall">
                            <Columns space="none" alignY="bottom">
                              <Column>
                                <FieldHeading
                                  heading={t(
                                    'write-page-branding-section-heading',
                                  )}
                                  secondaryHeading={t(
                                    'write-page-branding-section-secondary-heading',
                                  )}
                                />
                              </Column>
                              {isPrivateAdvertiser ? null : (
                                <Column width="content">
                                  <BrandingContextualHelp />
                                </Column>
                              )}
                            </Columns>
                            <Branding />
                          </Stack>
                        </Stack>
                      )}
                      <Video />
                    </Stack>
                  </PageSection>
                </Stack>
                <PageButtons
                  isLoading={isLoading}
                  showBackButton={true}
                  onBackButtonClicked={onBackButtonClicked}
                  handleSaveDraftFailed={handleSaveDraftFailed}
                  handleValidation={handlePageValidation}
                  showPreviewButton={true}
                />
              </Stack>
            </Form>
          </Stack>
        </PageLayout>
      </>
    );
  }

  return (
    <PageLayout
      hasProgressBar
      handleProgrammaticRouteChange={handleProgrammaticRouteChange}
      handleValidation={handlePageValidation}
      pageErrorMessage={pageErrorMessage}
      handleDispatchDraft={handleDispatchDraft}
      className="write-page-layout"
    >
      <Stack space="large">
        <Heading level="3" component="h2">
          {t('write-page-heading')}
        </Heading>
        <Form
          handleNextPageRoute={handleNextPageRoute}
          handleValidation={handlePageValidation}
        >
          <Stack space="xlarge">
            <Stack space={isKeySellingPointsEnabled ? 'small' : 'large'}>
              <PageSection sectionName={SECTION_JOB_DESCRIPTION}>
                <Stack space="small">
                  <Stack space="small">
                    <Stack space="medium">
                      <FieldHeading
                        heading={t(
                          'write-page-job-description-section-heading',
                        )}
                        subHeading={t(
                          'write-page-job-description-section-subHeading',
                        )}
                      />
                      <Box>
                        <AdWritingToggler
                          standardWritingFlowRef={standardWritingFlowRef}
                          guidedWritingFlowRef={guidedWritingFlowRef}
                        />
                      </Box>
                    </Stack>

                    {isGuided && <AboutBusiness ref={aboutBusinessRef} />}
                    <Stack space="xsmall">
                      <FieldHeading
                        heading={t('write-page-job-summary-input-label')}
                        subHeading={t(
                          'write-page-job-summary-text-description',
                        )}
                      />
                      <JobSummary ref={jobSummaryRef} />
                    </Stack>
                  </Stack>
                  {isKeySellingPointsEnabled && (
                    <KeySellingPoints ref={keySellingPointsRef} />
                  )}
                </Stack>
              </PageSection>
              <PageSection sectionName={SECTION_BRAND_SELECTION}>
                <Stack space="large">
                  {isBrandingEnabled && (
                    <Stack space="small">
                      <ProductFeatureBadge />
                      <Stack space="xsmall">
                        <Columns space="none" alignY="bottom">
                          <Column>
                            <FieldHeading
                              heading={t('write-page-branding-section-heading')}
                              secondaryHeading={t(
                                'write-page-branding-section-secondary-heading',
                              )}
                            />
                          </Column>
                          {isPrivateAdvertiser ? null : (
                            <Column width="content">
                              <BrandingContextualHelp />
                            </Column>
                          )}
                        </Columns>
                        <Branding />
                      </Stack>
                    </Stack>
                  )}

                  <Video />
                </Stack>
              </PageSection>
            </Stack>
            <PageButtons
              isLoading={isLoading}
              showBackButton={true}
              onBackButtonClicked={onBackButtonClicked}
              handleSaveDraftFailed={handleSaveDraftFailed}
              handleValidation={handlePageValidation}
              showPreviewButton={true}
            />
          </Stack>
        </Form>
      </Stack>
    </PageLayout>
  );
};

export default withDesktopEnhancers(PAGE_ID_WRITE)(WritePage);
